import React from "react";
import type { TFunction } from "i18next";
import { Mail } from "lucide-react";
import { Input, Label } from "../../../../components";
import type { ForgotPasswordFormMethods, ForgotPasswordFormValues } from "../types/forgot-password.types";

interface ForgotPasswordFormProps {
  t: TFunction;
  loading: boolean;
  error: string | null;
  retryAfterSeconds: number | null;
  form: ForgotPasswordFormMethods;
  onSubmit: (values: ForgotPasswordFormValues) => Promise<void>;
}

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({
  t,
  loading,
  error,
  retryAfterSeconds,
  form,
  onSubmit,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = form;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="mx-auto w-full max-w-[460px] space-y-5">
      <div>
        <Label
          htmlFor="email"
          className="block text-[15px] font-medium text-[#5c6472] mb-2"
        >
          {t("auth.email")}
        </Label>
        <div>
          <Input
            id="email"
            type="email"
            startIcon={<Mail className="h-4 w-4 text-[#7b8290]" />}
            placeholder={t("auth.enter_email_address")}
            disabled={loading}
            aria-invalid={errors.email ? "true" : "false"}
            aria-describedby={errors.email ? "email-error" : undefined}
            className={`h-[58px] border rounded-[12px] bg-white text-[#0f172a] text-[15px] placeholder:text-[#8a92a3] focus:outline-none focus:border-[#ff6b6b] focus:ring-2 focus:ring-[#ff6b6b]/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 ${
              errors.email ? "border-[#ff6b6b]" : "border-[#d7dbe3]"
            }`}
            {...register("email")}
          />
        </div>
        {errors.email && (
          <p
            id="email-error"
            role="alert"
            className="mt-2 text-[15px] text-[#ff5b5b]"
          >
            {errors.email.message}
          </p>
        )}
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-[12px]">
          <p className="text-sm text-red-600 text-center" role="alert">
            {error}
          </p>
        </div>
      )}

      <button
        type="submit"
        disabled={loading || (retryAfterSeconds !== null && retryAfterSeconds > 0)}
        className="
          w-full h-[56px]
          bg-gradient-to-r from-[#04c7a1] to-[#00b98e] hover:from-[#03b897] hover:to-[#00aa82]
          rounded-[16px]
          font-semibold text-white text-[16px]
          focus:outline-none focus:ring-2 focus:ring-[#00c49a] focus:ring-offset-2
          disabled:opacity-50 disabled:cursor-not-allowed
          transition-all duration-200
          transform hover:scale-[1.02] active:scale-[0.98]
        "
        >
        {loading ? (
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
            {t("auth.sending_reset_link")}
          </div>
        ) : retryAfterSeconds !== null && retryAfterSeconds > 0 ? (
          t("auth.send_reset_link") + ` (${retryAfterSeconds}s)`
        ) : (
          t("auth.send_reset_link")
        )}
      </button>
    </form>
  );
};

export default ForgotPasswordForm;
